import { useEffect, useMemo, useState } from 'react';

import { resolveBrandingLogoUrl } from '../../utils/brandingUrl';

type Size = 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<Size, string> = {
  sm: 'h-10 w-10 text-sm',
  md: 'h-20 w-20 text-lg',
  lg: 'h-24 w-24 text-xl',
};

function buildInitials(prenom?: string, nom?: string, fallbackName?: string) {
  const p = (prenom || '').trim().slice(0, 1);
  const n = (nom || '').trim().slice(0, 1);
  if (p || n) {
    return `${p}${n}`.toUpperCase() || '?';
  }
  const parts = (fallbackName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return '?';
}

type Props = {
  name?: string;
  prenom?: string;
  nom?: string;
  photoUrl?: string | null;
  /** Ancien champ : URL déjà résolue (ignoré si photoUrl est fourni). */
  avatarUrl?: string;
  size?: Size;
  className?: string;
};

export default function UserProfileAvatar({
  name = '',
  prenom,
  nom,
  photoUrl,
  avatarUrl,
  size = 'sm',
  className = '',
}: Props) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [photoUrl, avatarUrl]);

  const resolvedSrc = useMemo(() => {
    const fromPhoto = resolveBrandingLogoUrl(photoUrl ?? undefined);
    if (fromPhoto) return fromPhoto;
    const legacy = (avatarUrl || '').trim();
    if (!legacy) return undefined;
    return resolveBrandingLogoUrl(legacy) ?? legacy;
  }, [photoUrl, avatarUrl]);

  const showImage = Boolean(resolvedSrc) && !imageFailed;
  const initials = useMemo(() => buildInitials(prenom, nom, name), [prenom, nom, name]);

  return showImage ? (
    <img
      src={resolvedSrc}
      alt={name || 'Photo de profil'}
      className={['rounded-full border-2 border-white object-cover shadow-sm', SIZE_CLASS[size], className].join(' ')}
      onError={() => setImageFailed(true)}
    />
  ) : (
    <div
      className={[
        'flex shrink-0 items-center justify-center rounded-full border-2 border-white bg-primary/10 font-black text-primary shadow-sm',
        SIZE_CLASS[size],
        className,
      ].join(' ')}
      aria-hidden={!name}
      title={name || undefined}
    >
      {initials}
    </div>
  );
}
